import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Trash2, ChevronDown, ChevronUp, Users, GraduationCap } from 'lucide-react';
import { OBS_CRITERIA } from './TeamExercise';

const RATING_LABEL = { oui: '✔ Réussi', partiel: '~ Partiel', non: '✘ À travailler' };
const RATING_COLOR = { oui: '#16A34A', partiel: '#D97706', non: '#DC2626' };

const CorrectionsTab = () => {
  const [teams, setTeams] = useState([]);
  const [students, setStudents] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [teamName, setTeamName] = useState('');
  const [selectedMembers, setSelectedMembers] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [grades, setGrades] = useState({});

  const load = useCallback(async () => {
    try {
      const [tResp, sResp, subResp] = await Promise.all([
        axios.get(`${API}/teams`),
        axios.get(`${API}/rao/admin/students`),
        axios.get(`${API}/rao/admin/submissions`),
      ]);
      setTeams(tResp.data);
      setStudents(sResp.data || []);
      setSubmissions(subResp.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleMember = (st) => {
    setSelectedMembers(prev => prev.some(m => m.student_number === st.student_number)
      ? prev.filter(m => m.student_number !== st.student_number)
      : prev.length >= 3 ? (toast.error('Maximum 3 membres'), prev) : [...prev, { student_number: st.student_number, name: `${st.first_name} ${st.last_name}` }]);
  };

  const createTeam = async () => {
    if (!teamName.trim() || selectedMembers.length < 2) { toast.error('Nom + au moins 2 membres requis'); return; }
    await axios.post(`${API}/teams`, { name: teamName.trim(), members: selectedMembers, created_by: 'admin' });
    toast.success('Équipe créée');
    setTeamName(''); setSelectedMembers([]);
    load();
  };

  const deleteTeam = async (id) => {
    if (!window.confirm('Supprimer cette équipe?')) return;
    await axios.delete(`${API}/teams/${id}`).catch(() => {});
    load();
  };

  const gradeSubmission = async (sub) => {
    const g = grades[sub.id] || {};
    if (g.grade == null || g.grade === '') { toast.error('Entrez une note'); return; }
    await axios.put(`${API}/rao/admin/submissions/${sub.id}`, {
      grade: Number(g.grade),
      instructor_comments: g.comments || '',
    });
    toast.success('Correction enregistrée ✅');
    load();
  };

  const deleteSubmission = async (id) => {
    if (!window.confirm('Supprimer cette soumission?')) return;
    await axios.delete(`${API}/rao/admin/submissions/${id}`).catch(() => {});
    load();
  };

  const shown = submissions.filter(s => filter === 'all' || s.status === filter);

  const SessionSummary = ({ sub }) => {
    const s = sub.session;
    if (!s) return <p className="text-xs text-red-500">Session introuvable (supprimée?)</p>;
    if (sub.session_type === 'health') {
      return (
        <div className="text-xs text-gray-600 space-y-1">
          <p><strong>Protocole :</strong> {s.protocol_id} — mode {s.mode === 'guided' ? 'guidé' : 'simulation'} — durée {s.duration_seconds ? Math.floor(s.duration_seconds / 60) + ':' + String(s.duration_seconds % 60).padStart(2, '0') : '—'}</p>
          <p><strong>Priorité finale :</strong> P{s.final_priority || '—'} {s.p1_to_end_seconds != null && <span className="text-orange-700">• Volet P1 : délai {Math.floor(s.p1_to_end_seconds / 60)}:{String(s.p1_to_end_seconds % 60).padStart(2, '0')} — {Object.keys(s.pre_arrival?.instructions || {}).length} consignes données</span>}</p>
          {(s.red_flags_detected || []).length > 0 && <p className="text-red-600"><strong>Drapeaux rouges ({s.red_flags_detected.length}) :</strong> {s.red_flags_detected.slice(0, 3).join(' • ')}</p>}
        </div>
      );
    }
    return (
      <div className="text-xs text-gray-600 space-y-1">
        <p><strong>Scénario :</strong> {s.scenario_title || s.scenario_id} — opérateur : {s.operator_name}</p>
        <p><strong>Messages échangés :</strong> {(s.messages || []).length}</p>
        {(s.messages || []).length > 0 && (
          <details className="mt-1">
            <summary className="cursor-pointer text-blue-600 font-semibold">Voir la transcription</summary>
            <div className="mt-2 max-h-64 overflow-y-auto space-y-1 bg-white rounded-lg p-2 border">
              {s.messages.map((m, i) => (
                <p key={i} className={m.sender === 'operator' ? 'text-blue-800' : 'text-gray-700'}>
                  <strong>{m.sender === 'operator' ? '🎧 Répartiteur' : '📞 Citoyen'} :</strong> {m.text}
                </p>
              ))}
            </div>
          </details>
        )}
      </div>
    );
  };

  return (
    <div className="space-y-6" data-testid="corrections-tab">
      {/* ---- ÉQUIPES ---- */}
      <div className="bg-white rounded-2xl border p-6 shadow-sm">
        <h3 className="text-lg font-bold mb-4 flex items-center gap-2" style={{ color: '#002768' }}><Users className="w-5 h-5" /> Équipes ({teams.length})</h3>
        <div className="grid lg:grid-cols-2 gap-4">
          <div>
            <input value={teamName} onChange={e => setTeamName(e.target.value)} placeholder="Nom de l'équipe (ex : Équipe 1)"
              className="w-full px-3 py-2 rounded-lg border text-sm mb-2" data-testid="team-name-input" />
            <div className="max-h-44 overflow-y-auto border rounded-lg p-2 space-y-1">
              {students.map(st => {
                const sel = selectedMembers.some(m => m.student_number === st.student_number);
                return (
                  <button key={st.student_number} onClick={() => toggleMember(st)}
                    className={`w-full text-left px-2 py-1.5 rounded text-sm ${sel ? 'bg-blue-100 text-blue-800 font-semibold' : 'hover:bg-gray-50 text-gray-700'}`}
                    data-testid={`member-option-${st.student_number}`}>
                    {sel ? '✓ ' : ''}{st.first_name} {st.last_name} <span className="text-xs text-gray-400">#{st.student_number}</span>
                  </button>
                );
              })}
            </div>
            <button onClick={createTeam} className="w-full mt-2 py-2 rounded-lg text-white text-sm font-bold hover:opacity-90"
              style={{ backgroundColor: '#002768' }} data-testid="create-team-btn">
              Créer l'équipe ({selectedMembers.length}/3 membres)
            </button>
          </div>
          <div className="space-y-2">
            {teams.length === 0 && <p className="text-sm text-gray-400">Aucune équipe créée.</p>}
            {teams.map(t => (
              <div key={t.id} className="flex items-center justify-between p-3 rounded-lg bg-gray-50 border" data-testid={`team-row-${t.id}`}>
                <div className="text-sm">
                  <span className="font-bold text-gray-800">{t.name}</span>
                  <span className="text-gray-500"> — {t.members.map(m => m.name).join(', ')}</span>
                </div>
                <button onClick={() => deleteTeam(t.id)} className="p-1.5 rounded hover:bg-red-100" data-testid={`delete-team-${t.id}`}>
                  <Trash2 className="w-4 h-4 text-red-500" />
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ---- FILE DE CORRECTION ---- */}
      <div className="bg-white rounded-2xl border p-6 shadow-sm">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
          <h3 className="text-lg font-bold flex items-center gap-2" style={{ color: '#002768' }}><GraduationCap className="w-5 h-5" /> File de correction</h3>
          <div className="flex gap-1">
            {[['pending', 'À corriger'], ['corrected', 'Corrigées'], ['all', 'Toutes']].map(([k, label]) => (
              <button key={k} onClick={() => setFilter(k)}
                className={`px-3 py-1.5 rounded-full text-xs font-bold ${filter === k ? 'text-white' : 'bg-gray-100 text-gray-600'}`}
                style={filter === k ? { backgroundColor: '#002768' } : {}} data-testid={`filter-${k}`}>
                {label} ({submissions.filter(s => k === 'all' || s.status === k).length})
              </button>
            ))}
          </div>
        </div>

        {shown.length === 0 && <p className="text-sm text-gray-400" data-testid="no-submissions">Aucune soumission dans cette catégorie.</p>}
        <div className="space-y-3">
          {shown.map(sub => {
            const isOpen = expanded === sub.id;
            const g = grades[sub.id] || {};
            return (
              <div key={sub.id} className="border rounded-xl overflow-hidden" data-testid={`admin-submission-${sub.id}`}>
                <button onClick={() => setExpanded(isOpen ? null : sub.id)}
                  className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 text-left">
                  <div className="text-sm">
                    <span className="font-bold text-gray-800">{sub.team_name || 'Sans équipe'}</span>
                    <span className="text-gray-500"> — {sub.session_label} — répartiteur : {sub.dispatcher_name}</span>
                    <span className="text-xs text-gray-400 ml-2">{new Date(sub.submitted_at).toLocaleString('fr-CA', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    {sub.status === 'corrected'
                      ? <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-green-100 text-green-700">{sub.grade != null ? `${sub.grade}/100` : 'Corrigé'}</span>
                      : <span className="px-2 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-700">À corriger</span>}
                    {isOpen ? <ChevronUp className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
                  </div>
                </button>
                {isOpen && (
                  <div className="p-4 space-y-3">
                    {sub.team_note && <p className="text-xs bg-blue-50 border border-blue-200 rounded-lg p-2 text-blue-800"><strong>Note de l'équipe :</strong> {sub.team_note}</p>}
                    <SessionSummary sub={sub} />
                    {sub.observation && (
                      <div className="border rounded-lg p-3 bg-amber-50/50">
                        <p className="text-xs font-bold text-gray-700 mb-2">📋 Grille d'observation — par {sub.observation.observer_name}</p>
                        <div className="grid sm:grid-cols-2 gap-1.5">
                          {OBS_CRITERIA.map(c => {
                            const r = (sub.observation.criteria || {})[c.id] || {};
                            return (
                              <div key={c.id} className="text-xs flex items-start gap-1.5">
                                <span className="font-bold flex-shrink-0" style={{ color: RATING_COLOR[r.rating] || '#94A3B8' }}>{RATING_LABEL[r.rating] || '—'}</span>
                                <span className="text-gray-600">{c.label}{r.comment ? ` — « ${r.comment} »` : ''}</span>
                              </div>
                            );
                          })}
                        </div>
                        {sub.observation.global_comments && <p className="text-xs text-gray-600 mt-2 italic">« {sub.observation.global_comments} »</p>}
                      </div>
                    )}
                    <div className="flex flex-wrap items-end gap-2 pt-2 border-t">
                      <div>
                        <label className="text-xs font-semibold text-gray-500">Note /100</label>
                        <input type="number" min="0" max="100" value={g.grade ?? (sub.grade ?? '')}
                          onChange={e => setGrades(prev => ({ ...prev, [sub.id]: { ...prev[sub.id], grade: e.target.value } }))}
                          className="w-24 mt-0.5 px-3 py-2 rounded-lg border text-sm block" data-testid={`grade-input-${sub.id}`} />
                      </div>
                      <div className="flex-1 min-w-[220px]">
                        <label className="text-xs font-semibold text-gray-500">Commentaires pour l'équipe</label>
                        <input value={g.comments ?? (sub.instructor_comments || '')}
                          onChange={e => setGrades(prev => ({ ...prev, [sub.id]: { ...prev[sub.id], comments: e.target.value } }))}
                          className="w-full mt-0.5 px-3 py-2 rounded-lg border text-sm" data-testid={`grade-comments-${sub.id}`} />
                      </div>
                      <button onClick={() => gradeSubmission(sub)} className="px-4 py-2 rounded-lg text-white text-sm font-bold hover:opacity-90"
                        style={{ backgroundColor: '#16A34A' }} data-testid={`grade-btn-${sub.id}`}>
                        {sub.status === 'corrected' ? 'Mettre à jour' : 'Corriger ✔'}
                      </button>
                      <button onClick={() => deleteSubmission(sub.id)} className="p-2 rounded-lg hover:bg-red-100" data-testid={`delete-submission-${sub.id}`}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CorrectionsTab;
